import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/ampa-one-vs-neurostar-vs-brainsway — PROMPT 4
 * Owns "ampa one vs neurostar" and "ampa one vs brainsway" (Search Console
 * already shows these queries). All three are FDA-cleared for depression;
 * no head-to-head trial shows one works better than another.
 */

const AMPA_TRADEMARK =
  "Ampa and Ampa One are trademarks of Ampa Inc. Neuro Wellness TMS Centers of America is an independent provider and is not owned by Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "Ampa One vs NeuroStar: which is better?",
    answer:
      "No head-to-head randomized trial has shown one to be more effective than the other for depression. Both are FDA-cleared for major depressive disorder in adults who have not improved on antidepressants. The differences are coil design (Ampa One's lightweight 2.2 lb handheld coil with a built-in camera vs NeuroStar's figure-8 coil on a mechanical arm), how the coil is kept on target (camera neuronavigation vs head measurements), session length (minutes vs 19–37 minutes), and whether accelerated schedules are used. The clinic and protocol matter as much as the brand.",
  },
  {
    question: "Ampa vs BrainsWay for OCD?",
    answer:
      "BrainsWay is the device FDA-cleared for OCD (with its H7 coil). Ampa One is not cleared for OCD — it is cleared for major depressive disorder only. If OCD is your primary concern, BrainsWay is the cleared option. Be honest with yourself about this: a device cleared for your diagnosis is a better choice than a newer device used off-label.",
  },
  {
    question: "Ampa vs MagVenture?",
    answer:
      "MagVenture makes a figure-8 TMS system FDA-cleared for depression, used in conventional once-daily courses. Like NeuroStar, it is a standard TMS device on a mechanical arm with longer sessions. Ampa One differs with its lightweight handheld coil, built-in camera neuronavigation, and design for accelerated and one-day schedules. No head-to-head trial compares them.",
  },
  {
    question: "Is Ampa One as effective as conventional TMS?",
    answer:
      "No large randomized trial has compared Ampa One head-to-head with conventional TMS systems. All are FDA-cleared for depression based on demonstrating efficacy versus sham in their own trials. Ampa One's advantages are its short sessions, camera-guided targeting, and accelerated schedules; conventional systems have decades of use and broad insurance coverage. What matters most is that the device is cleared for your diagnosis and a psychiatrist supervises your care.",
  },
  {
    question: "Which TMS has the fewest side effects?",
    answer:
      "Across FDA-cleared TMS systems, the side-effect profile is similar: mild scalp discomfort and headache that usually fade, with a small risk of seizure. No device has been shown in head-to-head trials to have meaningfully fewer side effects. Shorter sessions (like Ampa One's minutes-long sessions) may be more comfortable simply because there is less time on the coil.",
  },
];

export const pageAmpaOneVsNeurostarVsBrainsway = makeArticle({
  slug: "ampa-one-vs-neurostar-vs-brainsway",
  title: "Ampa One vs NeuroStar vs BrainsWay: TMS Devices Compared (2026)",
  description:
    "Ampa One, NeuroStar, and BrainsWay compared by coil design, targeting, session length, FDA-cleared indications, and accelerated schedules. No head-to-head trial shows one is better.",
  canonical: "/learn/ampa-one-vs-neurostar-vs-brainsway",
  h1: "Ampa One vs NeuroStar vs BrainsWay: Which TMS Is Right for You?",
  breadcrumbLabel: "Ampa One vs NeuroStar vs BrainsWay",
  shortAnswer:
    "All three are FDA-cleared for depression that hasn't improved with medication. No head-to-head trial shows one device works better than another. The differences are coil design, how the coil is kept on target, session length, and what each is cleared for. The clinic and protocol matter as much as the brand. Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation.",
  keyTakeaways: [
    "All three are FDA-cleared for major depressive disorder in adults.",
    "Ampa One: 2.2 lb handheld coil with built-in camera, minutes-long sessions, designed for accelerated schedules.",
    "NeuroStar: figure-8 coil on a mechanical arm, 19–37 minute sessions, also cleared for anxious depression and adolescents 15–21.",
    "BrainsWay: H-coil helmet, deeper stimulation, the only one of the three cleared for OCD (H7 coil) and smoking cessation.",
    "No head-to-head trial shows one device is more effective than another.",
  ],
  sections: [
    {
      heading: "Quick answer",
      paragraphs: [
        "Ampa One, NeuroStar, and BrainsWay are all FDA-cleared TMS systems for depression that hasn't improved with medication. No head-to-head randomized trial has shown one to be more effective than another. The differences are coil design, how the coil is kept on target, session length, and what each is cleared for. The clinic and protocol matter as much as the brand.",
      ],
    },
    {
      heading: "Side by side",
      paragraphs: [],
      table: {
        headers: ["", "Ampa One", "NeuroStar", "BrainsWay"],
        rows: [
          ["Maker", "Ampa (2025)", "Neuronetics (2008)", "BrainsWay"],
          [
            "Coil",
            "Lightweight 2.2 lb figure-8-style coil with built-in camera",
            "Figure-8 coil on a mechanical arm",
            "H-coil in a helmet (deeper, broader stimulation)",
          ],
          [
            "Targeting",
            "Pre-printed cap + camera neuronavigation + Target Lock auto-pause",
            "Head measurements / positioning system",
            "Helmet positioning",
          ],
          [
            "Session length",
            "A few minutes",
            "About 19–37 minutes",
            "About 20 minutes",
          ],
          [
            "FDA-cleared for",
            "MDD (adults)",
            "MDD; anxious depression; adolescents 15–21",
            "MDD; OCD (H7 coil); smoking cessation",
          ],
          [
            "Accelerated / one-day schedules",
            "Designed for them",
            "Not routinely used",
            "Not routinely used",
          ],
          [
            "Available at Neuro Wellness",
            "Yes",
            "No (we do not offer NeuroStar)",
            "No (we do not offer BrainsWay)",
          ],
        ],
      },
    },
    {
      heading: "If you have OCD",
      paragraphs: [
        "BrainsWay is the device FDA-cleared for OCD, using its H7 coil. Ampa One is not cleared for OCD — it is cleared for major depressive disorder only. NeuroStar is not cleared for OCD either. If OCD is your primary concern, BrainsWay is the cleared option. Being honest about this builds trust: a device cleared for your diagnosis is a better choice than a newer device used off-label.",
      ],
    },
    {
      heading: "If schedule is your biggest barrier",
      paragraphs: [
        "Ampa One's short, minutes-long sessions and design for accelerated and one-day schedules make it the strongest fit when time is the obstacle. NeuroStar and BrainsWay are conventional once-daily systems over 4–6 weeks. See [[One-Day TMS in Florida|/one-day-tms]] and [[SAINT® vs Ampa One|/learn/saint-vs-ampa-one]].",
      ],
    },
    {
      heading: "Where SAINT® fits",
      paragraphs: [
        "SAINT® is a different kind of accelerated TMS: it uses an fMRI brain scan to personalize your target and runs over 5 days, with randomized-trial data behind it. We are the only Florida clinic offering both SAINT® and Ampa One. See [[SAINT® vs Ampa One|/learn/saint-vs-ampa-one]].",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Ampa Inc. Ampa One — FDA-cleared TMS system for major depressive disorder.",
      url: "https://www.ampahealth.com/",
    },
    {
      citation:
        "Neuronetics, Inc. NeuroStar Advanced Therapy TMS System — FDA clearances and indications.",
      url: "https://neurostar.com/",
    },
    {
      citation:
        "BrainsWay Ltd. Deep TMS System — FDA clearances and indications.",
      url: "https://brainsway.com/",
    },
  ],
  relatedLinks: [
    {
      title: "Ampa One™ TMS in Florida",
      href: "/ampa",
      description: "Camera-guided, accelerated depression treatment",
    },
    {
      title: "SAINT® vs Ampa One",
      href: "/learn/saint-vs-ampa-one",
      description: "Two accelerated TMS options, one Florida clinic",
    },
    {
      title: "TMS Devices Compared (full guide)",
      href: "/learn/tms-devices-compared-saint-neurostar-brainsway-exomind",
      description: "SAINT®, Ampa One, NeuroStar®, BrainsWay® & Exomind™",
    },
    {
      title: "Ampa One TMS Cost",
      href: "/learn/ampa-tms-cost",
      description: "Price, insurance, and payment options",
    },
    {
      title: "Schedule a Consultation",
      href: "/contactus",
      description: "Book a free consultation with our clinical team",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "Quick answer", href: "#quick-answer" },
    { label: "Side by side", href: "#side-by-side" },
    { label: "If you have OCD", href: "#if-you-have-ocd" },
    {
      label: "If schedule is your barrier",
      href: "#if-schedule-is-your-biggest-barrier",
    },
    { label: "Where SAINT® fits", href: "#where-saint-fits" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited research", href: "#cited-research" },
  ],
  safetyBlock:
    "Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation. Common side effects include mild scalp discomfort and headache that usually fade. If you are in crisis, call or text 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline: "Compare your options with our clinical team, free",
    primaryText: "Request a free consultation",
    primaryHref: "/ampa",
    secondaryText: "Call 954-827-9793",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
});
